import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Button, Card, Empty, Skeleton, Tag, Typography } from 'antd';
import { LeftOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';

import { portalService } from '../../services/api';
import '../../styles/ResponsavelFrequencia.css';

dayjs.locale('pt-br');

const { Title, Text } = Typography;

const STATUS_FREQUENCIA_LABELS = {
  PRESENTE: 'Presente',
  AUSENTE: 'Ausente',
  JUSTIFICADA: 'Falta justificada',
  NAO_INFORMADA: 'Chamada não informada',
};

const resolverErroDetalhe = (error) => {
  const status = error?.response?.status;
  if (status === 404) return 'Esta matrícula não foi encontrada ou não está disponível para você.';
  if (status === 403) return 'Você não possui acesso a esta área.';
  if (status === 422) return 'Não foi possível consultar o período informado.';
  return 'Não foi possível carregar a frequência. Tente novamente.';
};

const ResponsavelFrequenciaDetalhe = () => {
  const { matriculaId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const mesReferencia = useMemo(() => {
    const mesParam = searchParams.get('mes');
    const parsed = mesParam ? dayjs(mesParam, 'YYYY-MM') : null;
    return parsed && parsed.isValid() ? parsed.startOf('month') : dayjs().startOf('month');
  }, [searchParams]);

  const [detalhe, setDetalhe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState('');

  const carregarDetalhe = useCallback(async () => {
    setLoading(true);
    setErro('');
    try {
      const response = await portalService.obterDetalheFrequencia({
        matriculaId,
        dataInicio: mesReferencia.startOf('month').format('YYYY-MM-DD'),
        dataFim: mesReferencia.endOf('month').format('YYYY-MM-DD'),
      });
      setDetalhe(response.data || null);
    } catch (error) {
      setErro(resolverErroDetalhe(error));
    } finally {
      setLoading(false);
    }
  }, [matriculaId, mesReferencia]);

  useEffect(() => {
    carregarDetalhe();
  }, [carregarDetalhe]);

  const handleVoltar = () => {
    navigate('/responsavel/dashboard?tab=frequencia');
  };

  const itens = useMemo(() => detalhe?.itens || [], [detalhe]);

  const resumoRealizadas = useMemo(() => {
    const realizadas = itens.filter((item) => item.status_encontro === 'REALIZADA');
    return {
      total: realizadas.length,
      presentes: realizadas.filter((item) => item.status_frequencia === 'PRESENTE').length,
      ausentes: realizadas.filter((item) => item.status_frequencia === 'AUSENTE').length,
      justificadas: realizadas.filter((item) => item.status_frequencia === 'JUSTIFICADA').length,
      naoInformadas: realizadas.filter((item) => item.status_frequencia === 'NAO_INFORMADA').length,
    };
  }, [itens]);

  if (loading) {
    return (
      <div className="responsavel-frequencia-detalhe">
        <div role="status" aria-live="polite">
          <Text type="secondary">Carregando frequência…</Text>
          <Skeleton active paragraph={{ rows: 4 }} style={{ marginTop: 12 }} />
        </div>
      </div>
    );
  }

  if (erro) {
    return (
      <div className="responsavel-frequencia-detalhe">
        <Button
          icon={<LeftOutlined aria-hidden="true" />}
          onClick={handleVoltar}
          className="responsavel-frequencia-detalhe__voltar"
        >
          Voltar
        </Button>
        <Card>
          <Text>{erro}</Text>
          <div className="responsavel-frequencia-detalhe__erro-acoes">
            <Button type="primary" onClick={carregarDetalhe}>
              Tentar novamente
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="responsavel-frequencia-detalhe">
      <Button
        icon={<LeftOutlined aria-hidden="true" />}
        onClick={handleVoltar}
        className="responsavel-frequencia-detalhe__voltar"
      >
        Voltar
      </Button>

      <Title level={3} className="responsavel-frequencia-detalhe__aluno">
        {detalhe?.aluno_nome}
      </Title>
      <Text type="secondary" className="responsavel-frequencia-detalhe__periodo">
        {detalhe?.periodo_inicio} a {detalhe?.periodo_fim}
      </Text>

      <div className="responsavel-frequencia-detalhe__resumo" role="status" aria-live="polite">
        <Text>{resumoRealizadas.total} aula(s) realizada(s)</Text>
        <Text>{resumoRealizadas.presentes} presente(s)</Text>
        <Text>{resumoRealizadas.ausentes} ausência(s)</Text>
        <Text>{resumoRealizadas.justificadas} falta(s) justificada(s)</Text>
        {resumoRealizadas.naoInformadas > 0 && (
          <Text>{resumoRealizadas.naoInformadas} sem chamada informada</Text>
        )}
      </div>

      {itens.length === 0 ? (
        <Empty description="Não há aulas realizadas neste período." />
      ) : (
        <div className="responsavel-frequencia-detalhe__lista">
          {itens.map((item) => {
            const label = STATUS_FREQUENCIA_LABELS[item.status_frequencia] || item.status_frequencia;
            const provisorio = item.status_frequencia === 'NAO_INFORMADA';
            return (
              <Card key={item.encontro_id} className="responsavel-frequencia-detalhe__item">
                <div className="responsavel-frequencia-detalhe__item-info">
                  <Text strong>{dayjs(item.data_aula).format('DD/MM/YYYY')}</Text>
                  <Text type="secondary">
                    {String(item.hora_inicio).slice(0, 5)}–{String(item.hora_fim).slice(0, 5)}
                  </Text>
                  <Text type="secondary">{item.nome_turma}</Text>
                </div>
                <div className="responsavel-frequencia-detalhe__item-status">
                  <Tag>{label}</Tag>
                  {provisorio && <Text type="warning">Resultado provisório</Text>}
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ResponsavelFrequenciaDetalhe;
